import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { useIntl } from 'react-intl';
import { Row, Col, IconButton } from '@folio/stripes/components';
import EmptyData from '../../../../common/emptyData/EmptyData';
import { CHART_COLORS } from './GenericBarChart';
import { useChartTranslations } from '../../../../../helpers/useChartTranslations';
import { buildFormattingOptions, formatNumber, buildAnnotationMap, createFieldFormatter } from '../../../../../helpers/fieldValueFormatter';

const ZOOM_STEP = 5;
const MIN_RADIUS = 15;
const MAX_RADIUS = 80;

function GenericPieChart({
  resultSet,
  height = 600,
  colors = CHART_COLORS,
  showLegend = true,
  showTooltip = true,
  reportType,
  fieldAliasMap = {},
  formatting,
  chartOptions,
}) {
  const { translateLabel } = useChartTranslations(resultSet, reportType, fieldAliasMap);
  const intl = useIntl();
  const [radius, setRadius] = useState(MAX_RADIUS);
  const fmtOptions = buildFormattingOptions(formatting, intl);

  const effectiveColors = chartOptions?.colors?.length ? chartOptions.colors : colors;
  const effectiveLegend = chartOptions?.legend?.show !== undefined ? chartOptions.legend.show : showLegend;

  const seriesNames = resultSet?.seriesNames ? resultSet.seriesNames() : [];
  const dimKey = resultSet?.loadResponses?.[0]?.dimensions?.[0]?.replace('.', '_');
  const annotationMap = React.useMemo(() => buildAnnotationMap(resultSet), [resultSet]);

  const translatedData = React.useMemo(() => {
    const raw = resultSet?.chartPivot ? resultSet.chartPivot() : [];
    const xFormatter = dimKey ? createFieldFormatter(dimKey, annotationMap, { intl }) : null;
    if (!xFormatter) return raw;
    return raw.map((item) => ({
      ...item,
      x: xFormatter({ [dimKey]: item.x }) || item.x,
    }));
  }, [resultSet, annotationMap, dimKey, intl]);

  const chartData = React.useMemo(() => {
    if (!translatedData.length || !seriesNames.length) return translatedData;
    const MIN_PERCENT = 0.03;
    return translatedData.map((item) => {
      const result = { ...item };
      seriesNames.forEach(({ key }) => {
        const total = translatedData.reduce((s, d) => s + (parseFloat(d[key]) || 0), 0);
        const realVal = parseFloat(item[key]) || 0;
        result[`_real_${key}`] = realVal;
        result[key] = total > 0 ? Math.max(realVal, total * MIN_PERCENT) : realVal;
      });
      return result;
    });
  }, [translatedData, seriesNames]);

  if (!resultSet || !resultSet.loadResponses?.length || chartData.length === 0) {
    return <EmptyData />;
  }

  const tooltipFormatter = (value, name, props) => {
    const payload = props?.payload || {};
    const realKey = Object.keys(payload).find(k => k.startsWith('_real_'));
    const realVal = realKey ? payload[realKey] : value;
    return [formatNumber(realVal, fmtOptions), translateLabel(name)];
  };

  const handleZoomIn  = () => setRadius((r) => Math.min(r + ZOOM_STEP, MAX_RADIUS));
  const handleZoomOut = () => setRadius((r) => Math.max(r - ZOOM_STEP, MIN_RADIUS));

  return (
    <Row>
      {/* Pie column — zoom changes outerRadius only, pie stays centered */}
      <Col xs={effectiveLegend ? 10 : 12}>
        <div dir="ltr">
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <IconButton icon="plus-sign" onClick={handleZoomIn} disabled={radius >= MAX_RADIUS} />
            <IconButton icon="minus-sign" onClick={handleZoomOut} disabled={radius <= MIN_RADIUS} />
          </div>
          <ResponsiveContainer width="100%" height={height}>
            <PieChart>
              {seriesNames.map(({ key }) => (
                <Pie
                  key={key}
                  data={chartData}
                  dataKey={key}
                  nameKey="x"
                  cx="50%"
                  cy="50%"
                  outerRadius={`${radius}%`}
                  label={({ name }) => name}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={effectiveColors[index % effectiveColors.length]} />
                  ))}
                </Pie>
              ))}
              {showTooltip && <Tooltip formatter={tooltipFormatter} />}
            </PieChart>
          </ResponsiveContainer>
        </div>
      </Col>

      {/* Legend column — completely independent */}
      {effectiveLegend && (
        <Col xs={2}>
          <div style={{ overflowY: 'auto', maxHeight: '75vh', paddingInlineStart: '0.5rem' }}>
            {chartData.map((entry, index) => {
              const realVal = entry[`_real_${seriesNames[0]?.key}`] ?? entry[seriesNames[0]?.key];
              return (
                <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', gap: '8px' }}>
                  <span style={{
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: effectiveColors[index % effectiveColors.length],
                    flexShrink: 0,
                  }} />
                  <span style={{ fontSize: '0.85rem' }}>
                    {entry.x} ({formatNumber(realVal, fmtOptions)})
                  </span>
                </div>
              );
            })}
          </div>
        </Col>
      )}
    </Row>
  );
}

GenericPieChart.propTypes = {
  resultSet: PropTypes.object.isRequired,
  height: PropTypes.number,
  colors: PropTypes.arrayOf(PropTypes.string),
  showLegend: PropTypes.bool,
  showTooltip: PropTypes.bool,
  reportType: PropTypes.string,
  chartOptions: PropTypes.object,
};

export default GenericPieChart;
